// Which part of the area an event is in, read off the venue string.
//
// There is no area column. `venue` is free text - "Apocalypse Ale Works
// (Forest)", "Madison Heights Library", "1219 Main St" - so the area is derived
// at runtime, and every rule below is written against the venue strings actually
// sitting in lyh_events rather than a general-purpose gazetteer.
//
// Lynchburg is the fallback on purpose: this is a Lynchburg calendar, the
// outlying towns are what's worth naming, and most venues are a bare city street
// address. A venue we don't recognise (or a null one) is a city venue.

/** Display order: the city first, then its neighborhoods, then the outlying
    towns. Also the bound on how many rows the drawer's Location section can
    ever render. Neighborhood rows only show once a venue matches (Andrew,
    7/28), so the empty ones cost nothing until an event lands there. */
// Neighborhood set per lyhlovesyou.com/our-neighborhoods (Lisa, 7/28): the
// seven historic hill districts plus the named subdivisions. Skip-empty means
// the unmatched ones cost nothing until a venue lands in them.
export const AREAS = [
  'Lynchburg',
  'Boonsboro',
  'Cornerstone',
  'Courthouse Hill',
  "Daniel's Hill",
  'Diamond Hill',
  'Downtown',
  'Federal Hill',
  'Fort Hill',
  'Garland Hill',
  'Pierce Street',
  'Rivermont',
  'Sandusky',
  'Timberlake',
  'Wyndhurst',
  'Forest',
  'Madison Heights',
  'Amherst',
  'Appomattox',
  'Bedford',
  'Nelson',
  'Powhatan',
] as const

export type Area = (typeof AREAS)[number]

export interface AreaCount {
  area: Area
  count: number
}

// "Old Forest Rd" is a Lynchburg street, not the town of Forest - Grounded Earth
// at 3831 Old Forest Rd is inside the city. Checked first so a street name can
// never read as a town.
const LYNCHBURG_STREETS = ['Old Forest Rd']

// The town/county designator that means Bedford, VA - never a bare "Bedford"
// substring, which also matches the Lynchburg streets Bedford Ave and Bedford
// Rd (see areaForVenue).
const BEDFORD_TOKENS = [', Bedford', 'Bedford, VA', 'Bedford County']
// Towns on Smith Mountain Lake's Bedford-County shore, which read as Bedford
// on this calendar (Andrew, 8/19). Forest is its own existing area and is
// deliberately not included here.
const SML_BEDFORD_TOWNS = [
  'Huddleston',
  'Moneta',
  'Goodview',
  'Thaxton',
  'Montvale',
  'Stewartsville',
  'Big Island',
]

// Nelson County towns (regional catch-up scrape, 8/20/26): Wintergreen up on
// the Blue Ridge, the rest strung along Route 151 and the James River.
// Matched the same way as SML_BEDFORD_TOWNS - a bare town substring, checked
// against every approved venue string in the table first (none collide; the
// closest near-miss is "Café Monte Belle"/"Cafe Monte Bello", which doesn't
// contain the contiguous string "Montebello" because of the space).
const NELSON_TOWNS = [
  'Nellysford',
  'Afton',
  'Faber',
  'Roseland',
  'Lovingston',
  'Wintergreen',
  'Schuyler',
  'Arrington',
  'Shipman',
  'Piney River',
  'Massies Mill',
  'Tyro',
  'Montebello',
  'Gladstone',
  'Wingina',
]

// City neighborhoods (Andrew, 7/28), matched AFTER the outlying towns so
// "Amherst County Public Library, 382 S Main St" files under Amherst, not
// Downtown. Streets and names below are written against the venue strings in
// lyh_events today, same as everything else in this file.
const DOWNTOWN_STREETS = [
  'Main St',
  'Commerce St',
  'Jefferson St',
  'Church St',
  'Federal St',
  '9th St',
  '12th St',
]
// Downtown venues that sometimes appear as a bare name, no street address.
const DOWNTOWN_NAMES = [
  'Downtown',
  "Percival's Island",
  'Amphitheater',
  'Riverfront Park',
  'Academy',
  'Mythic Hall',
  'Trailhead',
  'Super Rad',
  'Hammer & Stain',
  // Named here since venue strings drop the street address when a name is
  // given (event-listing-template), and these two were only Downtown by
  // their Main St address (2026-09-27).
  'Firebrick Pottery',
  'The Vault',
]

/** The city neighborhoods, in AREAS order. They render INDENTED under Lynchburg
    in the drawer (Andrew, 7/28), because they are parts of the city rather than
    places beside it. Everything else in AREAS is an outlying town. */
export const LYNCHBURG_NEIGHBORHOODS: Area[] = [
  'Boonsboro',
  'Cornerstone',
  'Courthouse Hill',
  "Daniel's Hill",
  'Diamond Hill',
  'Downtown',
  'Federal Hill',
  'Fort Hill',
  'Garland Hill',
  'Pierce Street',
  'Rivermont',
  'Sandusky',
  'Timberlake',
  'Wyndhurst',
]

export const CITY: Area = 'Lynchburg'

export interface AreaGroups {
  /** null when the city has no events at all, itself or in a neighborhood.
      `count` is the WHOLE city: its own bucket plus every child below. */
  city: (AreaCount & { children: AreaCount[] }) | null
  /** Forest, Madison Heights, Amherst, Appomattox, Bedford, Nelson, Powhatan - flat,
      no parent. */
  towns: AreaCount[]
}

/**
 * Split a flat area list into the city (with its neighborhoods) and the towns.
 *
 * The city's count has to be the TOTAL, because areaForVenue files a venue under
 * exactly one area: a Downtown venue is 'Downtown' and NOT 'Lynchburg'. So the
 * bare 'Lynchburg' bucket is only the leftovers, and showing that number next to
 * a box labelled Lynchburg would promise 311 events and then hand over 418 once
 * the children came with it. Ticking the city ticks its neighborhoods too, for
 * the same reason - nobody should check Lynchburg and silently lose Downtown.
 */
export function splitAreas(areas: AreaCount[]): AreaGroups {
  const children = areas.filter((a) => LYNCHBURG_NEIGHBORHOODS.includes(a.area))
  const own = areas.find((a) => a.area === CITY)
  const total = (own?.count ?? 0) + children.reduce((n, c) => n + c.count, 0)
  return {
    city: total > 0 ? { area: CITY, count: total, children } : null,
    towns: areas.filter(
      (a) => a.area !== CITY && !LYNCHBURG_NEIGHBORHOODS.includes(a.area),
    ),
  }
}

export function areaForVenue(venue: string | null): Area {
  const v = venue ?? ''
  if (LYNCHBURG_STREETS.some((s) => v.includes(s))) return 'Lynchburg'
  if (v.includes('Forest')) return 'Forest'
  // Amherst Hwy (US-29 Business) runs through Madison Heights, so it has to be
  // claimed HERE, above the Amherst rule, or "4541 S Amherst Hwy" would file
  // under the town of Amherst instead.
  if (v.includes('Madison Heights') || v.includes('Amherst Hwy')) return 'Madison Heights'
  // Plain 'Amherst' rather than ', Amherst': the Amherst venues in the table lead
  // with the town ("Amherst County Public Library, 382 S Main St"), they don't
  // trail it after a comma, so the comma form matched nothing at all.
  if (v.includes('Amherst')) return 'Amherst'
  if (v.includes('Appomattox')) return 'Appomattox'
  // Bedford proper: matched on the city/county designator, never a bare
  // "Bedford" substring - "Bedford Ave" and "Bedford Rd" are Lynchburg
  // streets (Palmera House, 2307 Bedford Ave; Dollar General, 2303 Bedford
  // Ave), not the town of Bedford. Bower Center and the rest lead with the
  // town name or carry a ", Bedford"/"Bedford, VA"/"Bedford County" address
  // (Lisa asked for Bedford events, 7/28); skip-empty hides the row until one
  // lands. Smith Mountain Lake's Bedford-County shore towns file here too -
  // Forest is its own area already and is left alone.
  if (
    BEDFORD_TOKENS.some((t) => v.includes(t)) ||
    SML_BEDFORD_TOWNS.some((t) => v.includes(t))
  )
    return 'Bedford'
  // Nelson County: matched on the town, same rule as Bedford above. No token
  // here reads as a Lynchburg street or overlaps an existing approved venue.
  if (NELSON_TOWNS.some((t) => v.includes(t))) return 'Nelson'
  // Powhatan County, east of Richmond (~80 miles out, added 2026-10-04 for
  // Quarterfield Markets, the county library and the War Memorial Cultural Arts
  // Center). Matched on the bare town token, same rule as the towns above; no
  // approved venue string contained "Powhatan" when this was added.
  if (v.includes('Powhatan')) return 'Powhatan'
  if (v.includes('Boonsboro')) return 'Boonsboro'
  if (v.includes('Cornerstone')) return 'Cornerstone'
  if (v.includes('Timberlake')) return 'Timberlake'
  if (v.includes('Wyndhurst')) return 'Wyndhurst'
  // Historic-district neighborhoods, grounded in venue strings in the table:
  // Rivermont Ave venues + Riverside Park sit in the Rivermont district;
  // "Daniel's Hill Center, 317 Norwood St"; "Diamond Hill Center"; "1301
  // Pierce Street"; University of Lynchburg is the Fort Hill area. Checked
  // BEFORE Downtown so its broad street list can't claim them.
  if (v.includes('Rivermont') || v.includes('Riverside Park') || v.includes('Lynchburg Art Club'))
    return 'Rivermont'
  if (v.includes("Daniel's Hill") || v.includes('Norwood St')) return "Daniel's Hill"
  if (v.includes('Diamond Hill')) return 'Diamond Hill'
  if (v.includes('Pierce St')) return 'Pierce Street'
  if (v.includes('Fort Ave') || v.includes('University of Lynchburg')) return 'Fort Hill'
  if (v.includes('Sandusky')) return 'Sandusky'
  if (v.includes('Garland Hill')) return 'Garland Hill'
  if (v.includes('Federal Hill')) return 'Federal Hill'
  if (v.includes('Courthouse Hill') || v.includes('Monument Terrace') || v.includes('Court St'))
    return 'Courthouse Hill'
  if (DOWNTOWN_STREETS.some((s) => v.includes(s)) || DOWNTOWN_NAMES.some((s) => v.includes(s)))
    return 'Downtown'
  return 'Lynchburg'
}

/**
 * The TOWN a venue is in, which is what someone reading a place name wants
 * beside it. `areaForVenue` files a Main St venue under Downtown - the right
 * answer for a filter checkbox, and the wrong one for "where is this", where a
 * neighborhood reads as a city to anyone who doesn't already know Lynchburg.
 */
export function cityForVenue(venue: string | null): Area {
  const area = areaForVenue(venue)
  return LYNCHBURG_NEIGHBORHOODS.includes(area) ? CITY : area
}

/**
 * The POSTAL town a venue is in, for a mailing address rather than a filter.
 *
 * A third question about the same string, and it has to be its own function
 * because the other two deliberately answer it wrong. `areaForVenue` files by
 * checkbox, so a Main St venue is "Downtown". `cityForVenue` fixes that and
 * then collapses two whole counties on purpose: every Nelson town reads as
 * "Nelson" and Smith Mountain Lake's shore towns read as "Bedford", which is
 * how Andrew wants the area rows to group. Neither is a locality. Nelson is a
 * county with no town of that name in it, and Moneta and Huddleston are their
 * own USPS cities, not Bedford.
 *
 * The one consumer is the addressLocality in the Event structured data written
 * by scripts/generate-seo-pages.mjs, which is a claim about where a place is on
 * a public page. That script used to mirror this file's outlying-town rules by
 * hand, and it drifted: the mirror was written before the 8/19 Smith Mountain
 * Lake and 8/20 Nelson County scrapes added the towns above, so 282 events in
 * the current 104-day window - Afton, Lovingston, Moneta, Nellysford, Roseland
 * - were published with addressLocality "Lynchburg", up to 45 miles from where
 * they actually are. Same lists, same order, one file.
 */
export function localityForVenue(venue: string | null): Area | string {
  const v = venue ?? ''
  if (LYNCHBURG_STREETS.some((s) => v.includes(s))) return CITY
  if (v.includes('Forest')) return 'Forest'
  if (v.includes('Madison Heights') || v.includes('Amherst Hwy')) return 'Madison Heights'
  if (v.includes('Amherst')) return 'Amherst'
  if (v.includes('Appomattox')) return 'Appomattox'
  // The specific town, ahead of the ", Bedford" designator: "Layman Family
  // Farms, Montvale" is in Montvale, and only the area row calls it Bedford.
  const sml = SML_BEDFORD_TOWNS.find((t) => v.includes(t))
  if (sml) return sml
  if (BEDFORD_TOKENS.some((t) => v.includes(t))) return 'Bedford'
  const nelson = NELSON_TOWNS.find((t) => v.includes(t))
  if (nelson) return nelson
  if (v.includes('Powhatan')) return 'Powhatan'
  return CITY
}

/** Counts per area, in AREAS order, skipping areas with no events at all. */
export function areaCounts(venues: (string | null)[]): AreaCount[] {
  const m = new Map<Area, number>()
  for (const v of venues) {
    const a = areaForVenue(v)
    m.set(a, (m.get(a) ?? 0) + 1)
  }
  return AREAS.filter((a) => m.has(a)).map((area) => ({ area, count: m.get(area) ?? 0 }))
}
